import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatProgressBar } from '@angular/material/progress-bar'
import { AppService, Clinic, ClinicService, Patient, PAYMENT_PROVIDER, PaymentService, ProgressIndicatorService } from '@mmx/shared'
import { PaymentMethodCreateParams } from '@stripe/stripe-js'
import { switchMap } from 'rxjs'

import { environment } from '../../../../../environments/environment'
import {
  PaymentCardFormComponent,
  PaymentCardFormConfiguration,
  PaymentProviderConfiguration,
  TilledBillingDetail,
} from '../../payment-card-form'

@Component({
  selector: 'mmx-patient-credit-card-dialog',
  templateUrl: './patient-credit-card.component.html',
  styleUrls: ['./patient-credit-card.component.scss'],
  standalone: false,
})
export class PatientCreditCardComponent implements OnInit {
  @ViewChild(MatProgressBar) progressBar: MatProgressBar
  @ViewChild(PaymentCardFormComponent) paymentCardFormComponent: PaymentCardFormComponent

  clinic: Clinic
  patient: Patient
  cardForm: UntypedFormGroup
  useAddressOnFile = false
  isSaving = false
  paymentProviderConfiguration: PaymentProviderConfiguration
  cardFormConfiguration: PaymentCardFormConfiguration = {
    forcedSave: true,
  }

  get submitDisabled(): boolean {
    return this.cardForm.invalid || this.isSaving || !this.paymentCardFormComponent?.valid
  }

  constructor(
    private clinicService: ClinicService,
    public dialogRef: MatDialogRef<PatientCreditCardComponent>,
    public progressService: ProgressIndicatorService,
    private appService: AppService,
    private paymentService: PaymentService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.clinic = this.clinicService.clinic
    this.patient = this.data.patient
    this.paymentProviderConfiguration = {
      provider: this.clinic.paymentProvider.selectedProvider,
      locale: this.appService.currentLanguage,
      stripeAccount: this.clinic.paymentProvider?.id || '',
      stripePublicKey: environment.stripePublicKey,
      tilledId: this.clinic.paymentProvider.tilledId,
      tilledPublicKey: environment.tilledPublicKey,
      tilledProductionMode: environment.tilledProductionMode,
    }
    if (typeof this.data.forcedSave === 'boolean'){
      this.cardFormConfiguration.forcedSave = this.data.forcedSave
    }
  }

  ngOnInit() {
    this.cardForm = new UntypedFormGroup({
      holderName: new UntypedFormControl(
        this.patient.standardName,
        Validators.required,
      ),
      holderAddress: new UntypedFormControl(this.patient.address, Validators.required),
    })

    if (this.patient.hasAddress) {
      this.useAddressOnFile = true
    }
  }

  onRadioAddressSet() {
    this.useAddressOnFile = !this.useAddressOnFile

    if (this.useAddressOnFile) {
      this.cardForm.get('holderAddress').setValue(this.patient.address)
    } else {
      this.cardForm.get('holderAddress').setValue(null)
    }

    this.cardForm.get('holderName').setValue(this.patient.standardName)
  }

  saveCard() {
    this.isSaving = true
    if (
      !this.clinic.paymentProvider?.selectedProvider ||
      this.clinic.paymentProvider?.selectedProvider === PAYMENT_PROVIDER.STRIPE
    ) {
      this.saveCardStripe()
    } else {
      this.saveCardTilled()
    }
  }

  private saveCardStripe() {
    const progress = this.progressService.start()

    const { holderName, holderAddress } = this.cardForm.value

    const billingDetails: PaymentMethodCreateParams.BillingDetails = {
      name: holderName,
      address: {
        country: holderAddress.country,
        postal_code: holderAddress.zip,
        state: holderAddress.state,
        city: holderAddress.city,
        line1: holderAddress.address1,
        line2: holderAddress.address2,
      },
    }

    this.paymentService.createSetupIntent(this.patient.id).pipe(
      switchMap((clientSecret) => {
        return this.paymentCardFormComponent.confirmSetupIntent(clientSecret, billingDetails)
      }),
    ).subscribe({
      next: () => {
        this.dialogRef.close(true)
        this.isSaving = false
        this.progressBar.mode = 'determinate'
        progress.stop()
      },
      error: (error) => {
        const errorMessage = error.error?.message || 'Unable to add credit card. Please try again.'
        this.isSaving = false
        this.progressBar.mode = 'determinate'
        progress.stop(errorMessage)
      },
    })
  }

  private saveCardTilled() {
    const progress = this.progressService.start()
    const { holderName, holderAddress } = this.cardForm.value

    if (!holderAddress?.zip) {
      this.progressBar.mode = 'determinate'
      progress.stop('Please fill in the address fields.')
      this.isSaving = false
      return
    }

    let billingDetails: TilledBillingDetail = {
      name: holderName,
      email: this.patient.email,
      address: {
        country: holderAddress.country, // required
        zip: holderAddress.zip, // required
        state: holderAddress.state,
        city: holderAddress.city,
        street: holderAddress.address1,
      },
    }

    const errorHandler = (err) => {
      const errorMessage = err.message || 'Unable to add credit card. Please try again.'
      this.progressBar.mode = 'determinate'
      progress.stop(errorMessage)
    }

    this.paymentCardFormComponent
      .createTilledPaymentMethod(billingDetails)
      .then(({ id }) => {
        this.paymentService.attachTilledPaymentMethodToCustomer(id, this.patient.id).subscribe(
          {
            next: () => {
              this.progressBar.mode = 'determinate'
              this.isSaving = false
              progress.stop()
              this.dialogRef.close(true)
            },
            error: errorHandler,
          },
        )
      })
      .catch(errorHandler)
  }

  getErrorMessage(controlName, actualName) {
    if (this.cardForm.get(controlName).hasError('required')) {
      return `${actualName} is required`
    }

    return ''
  }
}
